import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';

/**
 * 資金繰り (キャッシュフロー) ページのデータ層フック。
 *
 * サーバー (get_cashflow) から「実績 months_back か月 + 予定 months_ahead か月」の
 * 資金繰り表マトリクスを取得する。各セルは value と source (出所) を持ち、
 * 実績 = OBC 仕訳の現預金増減、予定 = OBC 予定伝票を土台に買掛金支払 (Hozenall NET・
 * 工事管理の計上実データ) と販管費 (予算) を上書きしたもの。
 * セル明細 (取引先別 / 仕訳明細) は fetchDetail で都度取得する。
 */

export type CellSource = 'actual' | 'obc_plan' | 'payable' | 'budget' | 'average';

export type CashflowCell = {
  value: number;
  source: CellSource;
  count?: number;
  obc_plan?: number;
  version?: string;
};

export type CashflowMonth = { ym: number; label: string; kind: 'actual' | 'forecast' };

export type CashflowRow = {
  key: string;
  label: string;
  section: 'in' | 'out' | 'fin';
  cells: Record<number, CashflowCell>;
  children?: CashflowRow[];
  note?: string;
};

export type CashflowBalance = {
  opening: Record<number, number>;
  net: Record<number, number>;
  closing: Record<number, number>;
  bs_closing: Record<number, number | null>;
  diff: Record<number, number | null>;
};

export type CashflowMeta = {
  current_month: number;
  payable: { last_accrual_month: number | null; synced_at: string | null; row_count: number };
  budget_versions: Record<string, string>;
  generated_at: string;
};

export type CashflowMatrix = {
  months: CashflowMonth[];
  rows: CashflowRow[];
  balance: CashflowBalance;
  meta: CashflowMeta;
};

// 明細 (買掛予定は取引先別、それ以外は仕訳行)
export type PayableDetailItem = {
  source: string;
  supplier_code: string;
  supplier_name: string;
  count: number;
  gross_amount: number;
  amount: number;
  tax_amount: number;
  first_payment_date: string;
  last_payment_date: string;
  payment_method: string | null;
  terms_source: string;
  anken_nos: string;
};

export type JournalDetailItem = {
  slip_date: string;
  slip_no: string;
  direction: 'in' | 'out';
  counter_account_code: string;
  counter_account_name: string;
  amount: number;
  description: string;
  supplier_name: string | null;
  section_name: string | null;
};

export type CashflowDetail =
  | { kind: 'payable'; items: PayableDetailItem[] }
  | { kind: 'journal'; items: JournalDetailItem[] };

// 当月 (YYYY-MM)
const currentAnchor = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const shiftAnchor = (anchor: string, n: number): string => {
  const [y, m] = anchor.split('-').map(Number);
  const idx = y * 12 + (m - 1) + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
};

export function useCashflow() {
  const [matrix, setMatrix] = useState<CashflowMatrix | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 当月 (予定の起点)。前後に動かして過去の予定精度を振り返れる
  const [anchor, setAnchor] = useState<string>(currentAnchor());
  const monthsBack = 3;
  const monthsAhead = 6;

  const fetchMatrix = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(ep('get_cashflow'), {
        Process: 'Matrix',
        months_back: monthsBack,
        months_ahead: monthsAhead,
        anchor,
      });
      if (res.data?.status === 'success') {
        setMatrix(res.data.data as CashflowMatrix);
      } else {
        setError(res.data?.error || '資金繰り表の取得に失敗しました');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || '資金繰り表の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [anchor]);

  useEffect(() => { fetchMatrix(); }, [fetchMatrix]);

  const fetchDetail = useCallback(async (rowKey: string, ym: number, source: CellSource): Promise<CashflowDetail> => {
    const res = await axios.post(ep('get_cashflow'), { Process: 'Detail', row_key: rowKey, ym, source });
    if (res.data?.status !== 'success') {
      throw new Error(res.data?.error || '明細の取得に失敗しました');
    }
    return res.data.data as CashflowDetail;
  }, []);

  return {
    matrix,
    loading,
    error,
    anchor,
    setAnchor,
    monthsBack,
    monthsAhead,
    refresh: fetchMatrix,
    fetchDetail,
  };
}

export type UseCashflowResult = ReturnType<typeof useCashflow>;
